<script setup>
import config from "../config";
import { ref } from "vue";
import { mdiInformationVariantCircleOutline, mdiContentCopy, mdiCheckCircleOutline } from '@mdi/js'
import euIcon from "../assets/locale/eu.svg";
import ruIcon from "../assets/locale/ru.svg";

let snackbar = ref(false);

const endpoints = [
    { label: 'EU', host: 'eu', image: euIcon },
    { label: 'RU', host: 'ru', image: ruIcon },
];
const poolHost = config.ports[0]?.host ?? '';
const supportsCustomDifficulty = true;
const customDifficultyValue = (config.ports[0]?.diff ?? 1) * 2;
const customDifficultyPassword = `d=${customDifficultyValue}`;
const difficultyColors = {
    LOW: 'teal',
    MID: 'indigo',
    HIGH: 'deep-orange',
};
const difficultyBorderColors = {
    LOW: '#009688',
    MID: '#3f51b5',
    HIGH: '#ff5722',
};
const selectedEndpoint = ref(endpoints[0]);
const selectedPort = ref(config.ports[0] ?? null);

function selectPort(port) {
    selectedPort.value = port;
}

function selectEndpoint(endpoint) {
    selectedEndpoint.value = endpoint;
}

function connectionUrl() {
    return selectedPort.value
        ? `stratum+tcp://${selectedEndpoint.value.host}.${poolHost}:${selectedPort.value.port}`
        : '';
}

function difficultyColor(port) {
    return difficultyColors[port.info] ?? 'primary';
}

function difficultyBorderColor(port) {
    return difficultyBorderColors[port.info] ?? 'currentColor';
}

function success() {
    snackbar.value = true;
}
</script>
<template>
    <v-snackbar v-model="snackbar" location="top" color="success" min-width="150" min-height="30" :timeout="2000"
        class="pa-0">
        <v-icon left size="22" :icon="mdiCheckCircleOutline"></v-icon>
        {{ $t("copy_successful") }}
    </v-snackbar>
    <v-row dense>
        <v-col cols="12">
            <div class="text-h6 mb-2">
                {{ $t("сonnection_servers") }} TCP
            </div>
            <v-card class="location-card" variant="outlined">
                <v-card-text>
                    <div class="connection-steps">
                        <section class="connection-step">
                            <div class="connection-step-title">
                                <v-avatar size="24" color="surface-variant">1</v-avatar>
                                <span>{{ $t("choose_difficulty") }}</span>
                            </div>
                            <div class="selected-mode mb-3"
                                :style="{ borderColor: difficultyBorderColor(selectedPort) }">
                                <div class="selected-location-options mb-2">
                                    <v-btn v-for="endpoint in endpoints" :key="endpoint.host" size="x-small"
                                        class="location-button" rounded="md" height="30"
                                        :variant="selectedEndpoint.host === endpoint.host ? 'tonal' : 'outlined'"
                                        :color="selectedEndpoint.host === endpoint.host ? 'primary' : undefined"
                                        @click="selectEndpoint(endpoint)">
                                        <img v-if="endpoint.image" class="location-icon mr-2"
                                            :src="endpoint.image" alt="" />
                                        <v-icon v-else class="mr-2" size="18" :icon="endpoint.icon"></v-icon>
                                        <span>{{ endpoint.label }}</span>
                                    </v-btn>
                                </div>
                                <div class="d-flex align-center flex-wrap ga-2 mb-1">
                                    <span>{{ $t(selectedPort.info + "_BUTTON") }}</span>
                                    <v-code tag="span">{{ selectedPort.diff }}</v-code>
                                    (<span v-html="$filters.difficulty(selectedPort.diff, 0)"></span>)
                                </div>
                                <div class="text-body-2 py-3">
                                    {{ $t(selectedPort.info) }}
                                </div>
                            </div>
                            <div class="difficulty-options">
                                <v-btn v-for="item in config.ports" :key="item.port" size="small"
                                    class="difficulty-button" rounded="md" height="36"
                                    :variant="selectedPort.port === item.port ? 'tonal' : 'outlined'"
                                    :color="difficultyColor(item)" @click="selectPort(item)">
                                    <span>{{ $t(item.info + "_BUTTON") }}</span>
                                    <span class="ml-1 text-caption">
                                        · <span v-html="$filters.difficulty(item.diff, 0)"></span>
                                    </span>
                                </v-btn>
                            </div>
                        </section>

                        <section class="connection-step">
                            <div class="connection-step-title">
                                <v-avatar size="24" color="surface-variant">2</v-avatar>
                                <span>{{ $t("copy_connection") }}</span>
                            </div>
                            <div class="text-caption text-medium-emphasis mb-2">
                                {{ selectedEndpoint.label }} · {{ $t(selectedPort.info + "_BUTTON") }}
                            </div>
                            <div class="d-flex align-center ga-1">
                                <v-code tag="div" class="connection-url flex-grow-1">
                                    {{ connectionUrl() }}
                                </v-code>
                                <v-btn variant="text" size="small" :ripple="false" icon
                                    :key="connectionUrl()" :aria-label="$t('copy_connection')"
                                    v-clipboard="connectionUrl()" v-clipboard:success="success">
                                    <v-icon size="18" :icon="mdiContentCopy"></v-icon>
                                </v-btn>
                            </div>

                            <div class="hashrate-guide mt-4">
                                <div class="text-caption text-medium-emphasis mb-2">
                                    {{ $t("recommended_hashrate_guide") }}
                                </div>
                                <div class="hashrate-guide-current">
                                    <span>{{ $t(selectedPort.info + "_BUTTON") }}</span>
                                    <span class="text-medium-emphasis">
                                        {{ $t(selectedPort.info + "_HASHRATE") }}
                                    </span>
                                </div>
                                <div class="text-caption text-medium-emphasis mt-2">
                                    {{ $t("hashrate_guide_note") }}
                                </div>
                            </div>
                        </section>
                    </div>
                </v-card-text>
            </v-card>
        </v-col>
        <v-col cols="12">
            <div class="text-h6 mb-2">{{ $t("configuration") }}</div>
            <v-row dense>
                <v-col cols="12" md="7">
                    <v-card class="configuration-panel h-100" variant="outlined">
                        <v-card-text>
                            <p class="mb-4">{{ $t("configuration_info") }}</p>

                            <div class="configuration-step">
                                <div class="d-flex align-center flex-wrap ga-2 mb-2">
                                    <v-avatar size="24" color="surface-variant">1</v-avatar>
                                    <span>{{ $t("wallet_for_payouts") }}</span>
                                    <v-code tag="span">{{ $t("your_address") }}</v-code>
                                </div>
                                <div class="text-body-2 text-medium-emphasis">
                                    {{ $t("configuration_wallet", { msg: config.name }) }}
                                </div>
                                <v-code tag="div" class="configuration-value mt-2">{{ config.account }}</v-code>
                            </div>

                            <div class="configuration-step">
                                <div class="d-flex align-center flex-wrap ga-2 mb-2">
                                    <v-avatar size="24" color="surface-variant">2</v-avatar>
                                    <span>{{ $t("worker_identifier") }}</span>
                                    <v-code tag="span">{{ $t("worker_name") }}</v-code>
                                </div>
                                <div class="text-body-2 text-medium-emphasis">
                                    {{ $t("configuration_worker") }}
                                </div>
                                <v-code tag="div" class="configuration-value mt-2">Rig1</v-code>
                            </div>

                            <div class="configuration-step">
                                <div class="d-flex align-center flex-wrap ga-2 mb-2">
                                    <v-avatar size="24" color="surface-variant">3</v-avatar>
                                    <span>{{ $t("password_and_difficulty") }}</span>
                                    <v-code tag="span">{{ $t("password") }}</v-code>
                                </div>
                                <template v-if="supportsCustomDifficulty">
                                    <div class="text-body-2 text-medium-emphasis">
                                        {{ $t("configuration_password_custom", { value: customDifficultyPassword }) }}
                                    </div>
                                    <v-code tag="div" class="configuration-value mt-2">
                                        {{ customDifficultyPassword }}
                                    </v-code>
                                    <div class="custom-difficulty-note d-flex align-start ga-2 mt-3">
                                        <v-icon size="18" color="warning"
                                            :icon="mdiInformationVariantCircleOutline"></v-icon>
                                        <span class="text-body-2">{{ $t("configuration_password_minimum") }}</span>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="text-body-2 text-medium-emphasis">
                                        {{ $t("configuration_password_fixed") }}
                                    </div>
                                    <v-code tag="div" class="configuration-value mt-2">x</v-code>
                                </template>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" md="5">
                    <v-card class="configuration-panel h-100" variant="outlined">
                        <v-card-text>
                            <div class="text-subtitle-1 mb-4">{{ $t("connection_example") }}</div>
                            <div class="configuration-preview">
                                <div class="configuration-preview-row">
                                    <span class="text-caption text-medium-emphasis">URL</span>
                                    <v-code tag="div" class="configuration-value">
                                        stratum+tcp://{{ endpoints[0].host }}.{{ poolHost }}:{{ config.ports[0].port }}
                                    </v-code>
                                </div>
                                <div class="configuration-preview-row">
                                    <span class="text-caption text-medium-emphasis">Worker</span>
                                    <v-code tag="div" class="configuration-value">
                                        {{ $t("your_address") }}.{{ $t("worker_name") }}
                                    </v-code>
                                </div>
                                <div class="configuration-preview-row">
                                    <span class="text-caption text-medium-emphasis">Password</span>
                                    <v-code tag="div" class="configuration-value">
                                        {{ supportsCustomDifficulty ? customDifficultyPassword : "x" }}
                                    </v-code>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>
<style>
.connection-url {
    min-width: 0;
    overflow-wrap: anywhere;
}

.connection-steps {
    display: grid;
    grid-template-columns: minmax(520px, 1.6fr) minmax(320px, 1fr);
}

.connection-step {
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 0 16px;
}

.connection-step:first-child {
    padding-left: 0;
}

.connection-step:last-child {
    padding-right: 0;
}

.connection-step+.connection-step {
    border-left: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.connection-step-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.difficulty-button,
.location-button {
    min-width: 0 !important;
    padding-inline: 8px !important;
    letter-spacing: normal !important;
}

.difficulty-button .v-btn__content,
.location-button .v-btn__content,
.location-card .v-card-title {
    font-weight: 400 !important;
    text-transform: none !important;
}

.selected-location-options {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.location-icon {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    object-fit: cover;
}

.difficulty-options {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: auto;
}

.selected-mode {
    flex: 1;
    min-height: 112px;
    padding-left: 12px;
    border-left: 3px solid;
}

.hashrate-guide {
    padding-top: 12px;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.hashrate-guide-current {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 8px;
    border-radius: 6px;
    background: rgba(var(--v-theme-primary), .1);
}

.configuration-step+.configuration-step {
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.configuration-value {
    overflow-wrap: anywhere;
}

.custom-difficulty-note {
    padding: 10px 12px;
    border: 1px solid rgba(var(--v-theme-warning), .35);
    border-radius: 8px;
}

.configuration-preview {
    display: grid;
    gap: 16px;
}

.configuration-preview-row {
    display: grid;
    gap: 6px;
}

@media (max-width: 1050px) {
    .connection-steps {
        grid-template-columns: 1fr;
    }

    .connection-step {
        padding: 16px 0;
    }

    .connection-step:first-child {
        padding-top: 0;
    }

    .connection-step:last-child {
        padding-bottom: 0;
    }

    .connection-step+.connection-step {
        border-left: 0;
        border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    }
}

</style>
